import { createPortal } from "react-dom";
import React, { useEffect, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { X, Search, Sparkles } from "lucide-react";
import { SuggestDayPanel } from "./SuggestDayPanel";
import { KIND_OPTIONS, dayLabel, firstName } from "./boardUtils";

const cName = (c) => (c.name && c.name !== "?" ? c.name : c.company || c.phone || `Legacy #${c.legacy_id || ""}`);

export default function SuggestDayPopup({ onClose, onBooked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [client, setClient] = useState(null);
  const [address, setAddress] = useState("");
  const [addrQ, setAddrQ] = useState("");
  const [text, setText] = useState("");
  const [kind, setKind] = useState("");

  useEffect(() => {
    if (!q.trim() || client) { setResults([]); return; }
    const t = setTimeout(() => api("GET", `/clients?q=${encodeURIComponent(q)}&limit=8`).then((r) => setResults(r || [])).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [q, client]);

  const book = async (tech, date) => {
    if (!text.trim()) { toast.error("Type what the job is first"); return; }
    await api("POST", "/reminders", { text: text.trim(), due_date: date, assigned_to: tech, client_id: client?.id || null, on_board: true, kind: kind || null });
    toast.success(`Booked with ${firstName(tech)} on ${dayLabel(date)}`);
    onBooked();
  };

  return createPortal((
    <div className="fixed inset-0 z-[80] bg-slate-900/60 flex items-center justify-center p-3" onClick={onClose} data-testid="suggest-day-popup">
      <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <span className="font-display font-black text-slate-900 flex items-center gap-2"><Sparkles className="h-5 w-5 text-sky-600" />Suggest a Day</span>
          <button onClick={onClose} data-testid="suggest-day-close"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} data-testid="suggest-job-text" placeholder="What's the job? e.g. Pool service / clean / balance"
            className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="suggest-kind-select" className="w-full rounded-lg border-2 border-slate-200 px-2 py-1.5 text-sm">
            {KIND_OPTIONS.filter(([v]) => v !== "office" && v !== "away").map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {client ? (
            <div className="flex items-center justify-between rounded-xl border-2 border-sky-200 bg-sky-50 px-3 py-2">
              <span className="font-semibold text-sky-800 truncate" data-testid="suggest-client-chosen">{cName(client)}</span>
              <button onClick={() => { setClient(null); setQ(""); }}><X className="h-4 w-4 text-sky-600" /></button>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="suggest-client-search" placeholder="Search client name, phone…" className="w-full rounded-xl border-2 border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:border-sky-400" />
              {results.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                  {results.map((c) => <button key={c.id} data-testid={`suggest-client-result-${c.id}`} onClick={() => { setClient(c); setResults([]); setAddress(""); }} className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 truncate">{cName(c)}</button>)}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <input value={addrQ} onChange={(e) => setAddrQ(e.target.value)} data-testid="suggest-address-input" placeholder="…or type an address / suburb (new enquiry)" className="flex-1 rounded-xl border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" />
                <button onClick={() => setAddress(addrQ.trim())} disabled={!addrQ.trim()} data-testid="suggest-address-go" className="rounded-full bg-slate-900 text-white text-sm font-bold px-4 disabled:opacity-40">Find</button>
              </div>
            </div>
          )}
          {(client || address) && <SuggestDayPanel clientId={client?.id} address={client ? null : address} onBook={book} />}
        </div>
      </div>
    </div>
  ), document.body);
}
